import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  addTemplateTab,
  removeTemplateTab,
  updateTemplateTabName,
  setActiveTabId,
  updateTemplateDataCache,
  setTemplateDataCacheFull,
  setTabActiveView,
  setTabVmConfigValue,
  setIsFormVisible,
  setEditingTemplate,
  setActiveCreateType,
} from '../../../store/genbiSlice';
import GetCookie from '../../utils/GetCookie';
import ErrorPopup from '../../utils/popup/ErrorPopup';
import ProcessingPopup from '../../utils/popup/ProcessingPopup';
import SuccessPopup from '../../utils/popup/SuccessPopup';
import DeleteConfirmationModal from '../../utils/DeleteConfirmationModal';
import GenbiTemplateForm from './GenbiTemplateForm';
import GenbiTemplateList from './GenbiTemplateList';
import GenbiTemplateTabViewer from './GenbiTemplateTabViewer';
import axios from 'axios';
import GenbiLandingComponent from './GenbiLandingComponent';
import ListVM from './ListVM';
import GenbiReportView from './GenbiReportViewer';
/**
 * GenBI core workspace — shell-agnostic.
 *
 * Rendered by:
 *  - GenbiDashboard (standalone /genbi route)
 *  - ScreeningSpaceProjectView (embedded inside an SLR project)
 *
 * The shell (main-window / TopNav / SideNav / container-fluid) lives in the
 * parent.
 */
const GenbiWorkspace = ({
  slrEntry = null,        // { fromSLR, slrFabricId, slrTemplateName, templateId, action, templateStructure, reportDocsThreadId, fabricIdForTextractor }
  embedded = false,       // true when rendered inside an SLR project
  headerLeft = null,      // embedded: title + Create Template, above the template list
  headerActions = null,   // embedded: shell actions, right side of the tabs row
  onExit,                 // replaces navigate(-1) when the SLR form is cancelled
}) => {
  const dispatch = useDispatch();

  // ═══ Redux-persisted state (survives page navigation) ═══
  const {
    openTemplateTabs,
    activeTabId,
    templateDataCache,
    tabActiveViews,
    tabVmConfig,
    isFormVisible,
    editingTemplate,
    activeCreateType,
  } = useSelector((state) => state.genbi);

  // ═══ Local transient state (resets on mount — intentional) ═══
  const [preloadedTemplate, setPreloadedTemplate] = useState(null);
  const [genbiFabricId, setGenbiFabricId] = useState(() => slrEntry?.slrFabricId || null);
  const [fromSLR, setFromSLR] = useState(() => !!(slrEntry?.fromSLR && slrEntry?.slrFabricId));
  const [slrFabricId, setSlrFabricId] = useState(() => slrEntry?.slrFabricId || null);
  const [slrTemplateName, setSlrTemplateName] = useState(() => slrEntry?.slrTemplateName || null);
  const [reportDocsThreadId, setReportDocsThreadId] = useState(() => slrEntry?.reportDocsThreadId || null);

  const [showScrollArrows, setShowScrollArrows] = useState(false);
  const [fabricTreeData, setFabricTreeData] = useState([]);

  // Popup states
  const [isProcessingPopupVisible, setIsProcessingPopupVisible] = useState(false);
  const [processingMessage, setProcessingMessage] = useState('');
  const [isErrorPopupVisible, setIsErrorPopupVisible] = useState(false);
  const [popupErrorMessage, setPopupErrorMessage] = useState('');
  const [isSuccessPopupVisible, setIsSuccessPopupVisible] = useState(false);
  const [popupSuccessMessage, setPopupSuccessMessage] = useState('');

  const [isFormDirty, setIsFormDirty] = useState(false);
  // Delete confirmation modal states
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);

  // Refs
  const templateListRef = React.useRef();
  // Ref for GenbiTemplateForm — used to trigger save from outside
  const formRef = useRef(null);
  // Stores pending template to open after save-and-switch
  const pendingEditAfterSaveRef = useRef(null);

  const API_BASE_URL = '/pceapi/genbi';

  // ═══ Cache wrapper (keeps child components using setState pattern unchanged) ═══
  const handleSetTemplateDataCache = (updaterOrValue) => {
    if (typeof updaterOrValue === 'function') {
      const newValue = updaterOrValue(templateDataCache);
      dispatch(setTemplateDataCacheFull(newValue));
    } else {
      dispatch(setTemplateDataCacheFull(updaterOrValue));
    }
  };

  useEffect(() => {
    if (isFormVisible && editingTemplate) {
      handleProcessingStart(`Loading template: ${editingTemplate.template_name}`);
    }
  }, []);

  useEffect(() => {
    console.log("🔄 GenbiDashboard State Update:");
    console.log("   fromSLR:", fromSLR);
    console.log("   genbiFabricId:", genbiFabricId);
    console.log("   slrTemplateName:", slrTemplateName);
  }, [fromSLR, genbiFabricId, slrTemplateName]);

  useLayoutEffect(() => {
    const slrState = slrEntry;

    if (slrState?.fromSLR && slrState?.slrFabricId) {
      console.log("⚡ useLayoutEffect: Setting SLR states immediately", {
        fabricId: slrState.slrFabricId,
        templateName: slrState.slrTemplateName
      });

      setFromSLR(true);
      setGenbiFabricId(slrState.slrFabricId);
      setSlrFabricId(slrState.slrFabricId);
      setSlrTemplateName(slrState.slrTemplateName);
      setReportDocsThreadId(slrState.reportDocsThreadId || null);
    }
  }, [slrEntry]);

  // Refresh template list
  const refreshTemplateList = () => {
    if (templateListRef.current && templateListRef.current.fetchTemplates) {
      templateListRef.current.fetchTemplates();
    }
  };

  // Get active view for current tab
  const getActiveViewForTab = (tabId) => {
    return tabActiveViews[tabId] || 'template';
  };

  // Set active view for current tab → Redux
  const setActiveViewForTab = (tabId, view) => {
    dispatch(setTabActiveView({ tabId, view }));
  };

  // ═══ Create Template → Opens Submission Report form directly ═══
  const handleCreateTemplate = () => {
    dispatch(setEditingTemplate(null));
    dispatch(setActiveCreateType('SUBMISSION_REPORT'));
    dispatch(setIsFormVisible(true));
    setIsFormDirty(false);
  };

  // ═══ Edit template handler ═══
  const handleEditTemplate = async (template, fabric_id, fabric_name) => {
    handleProcessingStart(`Loading template: ${template.template_name}`);

    try {
      const enhancedTemplate = {
        ...template,
        fabric_id,
        fabric_name,
        vm_config: template.vm_config || false,
        lang_id: template.lang_id || 1
      };

      dispatch(setActiveCreateType('SUBMISSION_REPORT'));
      dispatch(setEditingTemplate(enhancedTemplate));
      dispatch(setIsFormVisible(true));
      setIsFormDirty(false);
    } catch (error) {
      handleProcessingEnd();
      setPopupErrorMessage('Failed to load template details');
      setIsErrorPopupVisible(true);
    }
  };

  // ═══ Open template in new tab ═══
  const handleOpenTemplate = (template, fabric_id, fabric_name) => {
    const existingTab = openTemplateTabs.find(tab => tab.id === template.id);

    if (!existingTab) {
      const newTab = {
        id: template.id,
        template_name: template.template_name,
        fabric_id: fabric_id,
        fabric_name: fabric_name,
        template_type: 'SUBMISSION_REPORT',
        vm_config: template.vm_config || false,
        lang_id: template.lang_id || 1
      };
      dispatch(addTemplateTab(newTab));
      dispatch(setActiveTabId(template.id));
      dispatch(setTabActiveView({ tabId: template.id, view: 'template' }));
      dispatch(setTabVmConfigValue({ tabId: template.id, config: template.vm_config === true }));
    } else {
      dispatch(setActiveTabId(template.id));
    }
  };

  // Load and open existing template (for SLR navigation)
  const loadAndOpenExistingTemplate = async (templateId, fabricId, templateName) => {
    try {
      handleProcessingStart(`Loading template: ${templateName}`);

      const existingTab = openTemplateTabs.find(tab => tab.id === templateId);

      if (!existingTab) {
        const response = await axios.post(
          `${API_BASE_URL}/get-template-details`,
          {
            template_id: templateId,
            fabric_id: fabricId,
            include_agent_responses: true
          },
          { headers: { Authorization: GetCookie('access_token') } }
        );

        if (response.data.status === 'success') {
          const templateData = response.data.data;

          const newTab = {
            id: templateId,
            template_name: templateData.template_name,
            fabric_id: fabricId,
            fabric_name: templateData.fabric_name,
            template_type: 'SUBMISSION_REPORT',
            vm_config: templateData.vm_config || false,
            lang_id: templateData.lang_id || 1
          };

          dispatch(addTemplateTab(newTab));
          dispatch(setActiveTabId(templateId));
          dispatch(setTabActiveView({ tabId: templateId, view: 'template' }));
          dispatch(setTabVmConfigValue({ tabId: templateId, config: templateData.vm_config === true }));
          dispatch(updateTemplateDataCache({ templateId, data: templateData }));
        }
      } else {
        dispatch(setActiveTabId(templateId));
      }

      handleProcessingEnd();

    } catch (error) {
      handleProcessingEnd();
      console.error("Failed to load template:", error);
      setPopupErrorMessage('Failed to load template from SLR');
      setIsErrorPopupVisible(true);
    }
  };

  // Close template tab → single Redux action handles all cleanup
  const handleCloseTab = (templateId) => {
    dispatch(removeTemplateTab(templateId));
  };

  // Handle delete request from template list
  const handleDeleteRequest = (template, fabricId, fabricName) => {
    const templateInfo = {
      ...template,
      fabric_id: fabricId,
      fabric_name: fabricName
    };

    setTemplateToDelete(templateInfo);
    setShowDeleteConfirm(true);
  };

  // ═══ Handle delete confirmation ═══
  const handleConfirmDelete = async () => {
    setShowDeleteConfirm(false);

    if (!templateToDelete) return;

    const isCurrentlyEditing = isFormVisible && editingTemplate?.id === templateToDelete.id;

    if (isCurrentlyEditing) {
      if (!window.confirm(`You are currently editing this template. It will be deleted. Proceed?`)) {
        setTemplateToDelete(null);
        return;
      }
    }

    handleProcessingStart(`Deleting template ${templateToDelete.template_name}`);

    try {
      const response = await axios.post('/pceapi/genbi/delete-template', {
        template_id: templateToDelete.id,
        fabric_id: templateToDelete.fabric_id
      }, {
        headers: { Authorization: GetCookie('access_token') }
      });

      handleProcessingEnd();

      if (response.data.status === 'failed') {
        setPopupErrorMessage(response.data.message);
        setIsErrorPopupVisible(true);
      } else {
        if (isCurrentlyEditing) {
          dispatch(setIsFormVisible(false));
          dispatch(setEditingTemplate(null));
          dispatch(setActiveCreateType(null));
        }

        handleCloseTab(templateToDelete.id);

        setPopupSuccessMessage(response.data.message || 'Template deleted successfully');
        setIsSuccessPopupVisible(true);
      }
    } catch (error) {
      handleProcessingEnd();
      setPopupErrorMessage('Failed to delete template');
      setIsErrorPopupVisible(true);
    } finally {
      setTemplateToDelete(null);
    }
  };

  const handleDeleteTemplate = async (templateId, fabricId, templateName, isCurrentTemplate = false) => {
    if (isCurrentTemplate && editingTemplate && editingTemplate.id === templateId) {
      dispatch(setIsFormVisible(false));
      dispatch(setEditingTemplate(null));
      dispatch(setActiveCreateType(null));
    }
  };

  const handleProcessingStart = (message) => {
    setProcessingMessage(message);
    setIsProcessingPopupVisible(true);
  };

  const handleProcessingEnd = () => {
    setIsProcessingPopupVisible(false);
    setProcessingMessage('');
  };

  const closeErrorPopup = () => {
    setIsErrorPopupVisible(false);
  };

  const closeSuccessPopup = () => {
    setIsSuccessPopupVisible(false);
    if (templateListRef.current && templateListRef.current.fetchTemplates) {
      templateListRef.current.fetchTemplates();
    }
  };

  // Load fabric documents
  const loadFabricDocuments = async (fabricId) => {
    try {
      const response = await axios.post(
        '/pceapi/genbi/fabric_unstructured_data_tree',
        {
          fabric_id: fabricId,
          include_documents: true,
          page: 1,
          size: 25
        },
        {
          headers: {
            Authorization: GetCookie('access_token')
          }
        }
      );

      if (response.data.status === 'success') {
        setFabricTreeData(response.data.data);
      }
    } catch (error) {
      console.error('Error loading fabric documents:', error);
    }
  };

  // Load fabric docs when switching to valuemessage view
  useEffect(() => {
    const currentTab = openTemplateTabs.find(tab => tab.id === activeTabId);
    const activeView = getActiveViewForTab(activeTabId);

    if (currentTab?.fabric_id && activeView === 'valuemessage') {
      loadFabricDocuments(currentTab.fabric_id);
    }
  }, [activeTabId, tabActiveViews, openTemplateTabs]);

  // Sync vm_config from cache
  useEffect(() => {
    if (activeTabId && templateDataCache[activeTabId]) {
      dispatch(setTabVmConfigValue({
        tabId: activeTabId,
        config: templateDataCache[activeTabId].vm_config === true
      }));
    }
  }, [activeTabId, templateDataCache]);

  // Auto-scroll to active tab
  useEffect(() => {
    if (activeTabId && openTemplateTabs.length > 0) {
      setTimeout(() => {
        const activeButton = document.querySelector(`.tabbtn[data-tab-id="${activeTabId}"]`);
        if (activeButton) {
          activeButton.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center'
          });
        }
      }, 100);
    }
  }, [activeTabId, openTemplateTabs.length]);

  // Detect tab overflow for scroll arrows
  useEffect(() => {
    const checkOverflow = () => {
      const container = document.querySelector('.tabs-scroll-wrapper');
      if (container) {
        const hasOverflow = container.scrollWidth > container.clientWidth;
        setShowScrollArrows(hasOverflow);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);

    return () => window.removeEventListener('resize', checkOverflow);
  }, [openTemplateTabs]);

  // Handle SLR navigation
  useEffect(() => {
    const slrState = slrEntry;

    if (slrState?.fromSLR && slrState?.templateId) {
      const action = slrState.action;
      const templateId = slrState.templateId;
      const fabricIdToUse = slrState.slrFabricId;

      console.log("🎯 GenBI: Processing SLR navigation (after layout)", {
        action,
        templateId,
        fabricId: fabricIdToUse
      });

      if (action === "created") {
        console.log("GenBI: Loading create form with structure");

        // Open tab directly (data fetches on render)
        handleOpenTemplate(
          {
            id: templateId,
            template_name: slrState.slrTemplateName
          },
          fabricIdToUse,
          null
        );
      } else if (action === "opened") {
        console.log("GenBI: Opening existing template");

        loadAndOpenExistingTemplate(templateId, fabricIdToUse, slrState.slrTemplateName);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slrEntry]);

  // ═══ Render tab content based on active view ═══
  const renderTabContent = (tabId) => {
    const activeView = getActiveViewForTab(tabId);
    const currentTab = openTemplateTabs.find(tab => tab.id === tabId);

    if (!currentTab) return null;

    switch (activeView) {
      case 'template':
        return (
          <GenbiTemplateTabViewer
            templateId={tabId}
            templateDataCache={templateDataCache}
            fabricId={currentTab.fabric_id}
            templateName={currentTab.template_name}
            onTemplateNameSaved={(newName) => dispatch(updateTemplateTabName({ tabId, template_name: newName }))}
            setTemplateDataCache={handleSetTemplateDataCache}
            onError={(message) => {
              setPopupErrorMessage(message);
              setIsErrorPopupVisible(true);
            }}
            onProcessingStart={handleProcessingStart}
            onProcessingEnd={handleProcessingEnd}
            onRefreshTemplateList={refreshTemplateList}
            vmConfig={tabVmConfig[tabId]}
            langId={currentTab.lang_id || 1}
            isFormVisible={isFormVisible}
            onCreateTemplate={handleCreateTemplate}
            onTemplateNavigationBack={() => {
              dispatch(setIsFormVisible(false));
              dispatch(setEditingTemplate(null));
              dispatch(setActiveCreateType(null));
            }}
          />
        );
      case 'valuemessage':
        return (
          <div>
            <ListVM
              selectedTemplate={tabId}
              fabric_id={currentTab.fabric_id}
              fabricTreeData={fabricTreeData}
            />
          </div>
        );
      case 'report':
        return (
          <GenbiReportView
            templateId={tabId}
            onError={(message) => {
              setPopupErrorMessage(message);
              setIsErrorPopupVisible(true);
            }}
            onProcessingStart={handleProcessingStart}
            onProcessingEnd={handleProcessingEnd}
            onRefreshTemplateList={refreshTemplateList}
            reportDocsThreadId={reportDocsThreadId}
          />
        );
      default:
        return null;
    }
  };

  return (
    <React.Fragment>
      <div className={embedded ? 'slr-genbi-root' : undefined}>
        <div className={embedded ? 'genbi-layout-row slr-genbi-embedded' : 'genbi-layout-row'}>
                {/* Template list column — title + list together, always in-flow */}
                <div className="genbi-template-list-col">

                  {/* Title + Create button — scoped above template list only */}
                  <div className="genbi-row-header">
                    <p className="gradient-font5 ev-fs-18 ev-bold-med mb-0">
                      Gen BI Studio
                    </p>
                    {!isFormVisible && (
                      <div className="position-relative">
                        <button
                          type="button"
                          className="ev-btn-yellow-fill-round ev-bold-reg"
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={handleCreateTemplate}
                        >
                          <i className="bi bi-plus-circle me-1"></i> Create Template
                        </button>
                      </div>
                    )}
                    {isFormVisible && (
                      <div className="position-relative">
                        <button
                          type="button"
                          className="ev-btn-lightblue-fill-round ev-bold-reg"
                          style={{ whiteSpace: 'nowrap' }}
                          onClick={() => {
                            dispatch(setIsFormVisible(false));
                            dispatch(setEditingTemplate(null));
                            dispatch(setActiveCreateType(null));
                          }}
                        >
                          <i className=" me-1"></i> Template Navigation
                        </button>
                      </div>
                    )}
                  </div>
                  <GenbiTemplateList
                    ref={templateListRef}
                    disableInitialFetch={fromSLR}
                    onEditTemplate={handleEditTemplate}
                    onOpenTemplate={handleOpenTemplate}
                    onRefresh={() => { }}
                    onDeleteTemplate={handleDeleteTemplate}
                    onDeleteRequest={handleDeleteRequest}
                    openTemplateIds={openTemplateTabs.map(tab => tab.id)}
                    onError={(message) => {
                      setPopupErrorMessage(message);
                      setIsErrorPopupVisible(true);
                    }}
                    isFormVisible={isFormVisible}
                    editingTemplateId={editingTemplate?.id || null}
                    editingTemplateName={editingTemplate?.template_name || ''}
                    isFormDirty={isFormDirty}
                    onSaveTemplate={(pendingEdit) => {
                      pendingEditAfterSaveRef.current = pendingEdit || null;
                      formRef.current?.submitForm();
                    }}
                    onProcessingStart={handleProcessingStart}
                    onProcessingEnd={handleProcessingEnd}
                    slrFilterMode={fromSLR}
                    genbiFabricId={genbiFabricId}
                    slrTemplateName={slrTemplateName}
                    onClearFilter={() => {
                      console.log("🧹 Clearing SLR filter");
                      setFromSLR(false);
                      setGenbiFabricId(null);
                      setSlrTemplateName(null);
                      setFromSLR(false);
                      setSlrTemplateName(null);
                      if (templateListRef.current?.fetchTemplates) {
                        templateListRef.current.fetchTemplates();
                      }
                    }}
                  />
                </div>

                {/* Main content column */}
                <div className="genbi-main-content-col">
                {/* Header slot — tabs when present, empty when landing/form */}
                <div className="genbi-row-header">
                  {!isFormVisible && openTemplateTabs.length > 0 ? (
                    <div className="nav-tabs-container">
                      {/* Left Arrow */}
                      {showScrollArrows && (
                        <button
                          className="ev-btn-lightblue-fill-round ev-icon-btn-padding-small"
                          onClick={() => {
                            const currentIndex = openTemplateTabs.findIndex(tab => tab.id === activeTabId);
                            if (currentIndex > 0) {
                              dispatch(setActiveTabId(openTemplateTabs[currentIndex - 1].id));
                            }
                          }}
                          disabled={openTemplateTabs.length <= 1}
                        >
                          <i className="bi bi-chevron-left"></i>
                        </button>
                      )}
                      <div className="tabs-scroll-wrapper">
                        {openTemplateTabs.map(tab => (
                          <button
                            key={tab.id}
                            data-tab-id={tab.id}
                            className={`avatar-tooltip tabbtn tabbtn-round m-0 me-1 ${activeTabId === tab.id ? 'tabbtn-active' : 'tabbtn-inactive'}`}
                            onClick={() => dispatch(setActiveTabId(tab.id))}
                            data-tooltip={tab.template_name}
                          >
                            {tab.template_name.length > 15
                              ? tab.template_name.substring(0, 15) + '...'
                              : tab.template_name}
                            <button
                              className="tabbtn-close ms-2"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCloseTab(tab.id);
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </button>
                        ))}
                      </div>
                      {showScrollArrows && (
                        <button
                          className="ev-btn-lightblue-fill-round ev-icon-btn-padding-small"
                          onClick={() => {
                            const currentIndex = openTemplateTabs.findIndex(tab => tab.id === activeTabId);
                            if (currentIndex < openTemplateTabs.length - 1) {
                              dispatch(setActiveTabId(openTemplateTabs[currentIndex + 1].id));
                            }
                          }}
                          disabled={openTemplateTabs.length <= 1}
                        >
                          <i className="bi bi-chevron-right"></i>
                        </button>
                    )}
                    </div>
                  ) : <div />}
                  {headerActions}
                </div>
                <div className="inner-window1">
                  {isFormVisible ? (
                    <>
                      {/* Submission Report Form */}
                      {activeCreateType === 'SUBMISSION_REPORT' && (
                        <GenbiTemplateForm
                          ref={formRef}
                          editingTemplate={editingTemplate}
                          preloadedTemplate={preloadedTemplate}
                          fromSLR={fromSLR}
                          selectedTemplate={editingTemplate?.id || null}
                          onDirtyChange={setIsFormDirty}
                          onSuccess={(message, updatedLangId, savedTemplateName) => {
                            if (editingTemplate?.id) {
                              // Sync tab name immediately
                              if (savedTemplateName) {
                                dispatch(updateTemplateTabName({ tabId: editingTemplate.id, template_name: savedTemplateName }));
                              }
                              handleSetTemplateDataCache(prev => {
                                const newCache = { ...prev };
                                delete newCache[editingTemplate.id];
                                return newCache;
                              });
                            }
                            setIsFormDirty(false);
                            setPopupSuccessMessage(message);
                            setIsSuccessPopupVisible(true);
                          const pending = pendingEditAfterSaveRef.current;
                          pendingEditAfterSaveRef.current = null;
                          if (pending) {
                            dispatch(setIsFormVisible(false));
                            dispatch(setEditingTemplate(null));
                            setTimeout(() => {
                              handleEditTemplate(pending.template, pending.fabricId, pending.fabricName);
                            }, 50);
                          } else {
                            if (updatedLangId && editingTemplate) {
                              dispatch(setEditingTemplate({ ...editingTemplate, lang_id: updatedLangId }));
                            }
                            dispatch(setIsFormVisible(false));
                            dispatch(setEditingTemplate(null));
                          }
                            dispatch(setActiveCreateType(null));
                            setPreloadedTemplate(null); 
                            setFromSLR(false); 
                          }}
                          onError={(message) => {
                            setPopupErrorMessage(message);
                            setIsErrorPopupVisible(true);
                          }}
                          onCancel={() => {
                            dispatch(setIsFormVisible(false));
                            dispatch(setEditingTemplate(null));
                            dispatch(setActiveCreateType(null));
                            setPreloadedTemplate(null); 
                            
                            if (fromSLR) {
                              onExit?.();
                            }
                            
                            setFromSLR(false); 
                          }}
                          onProcessingStart={handleProcessingStart}
                          onProcessingEnd={handleProcessingEnd}
                          onTemplateCreated={(templateId, fabricId, templateName) => {
                            dispatch(setIsFormVisible(false));
                            dispatch(setEditingTemplate(null));
                            dispatch(setActiveCreateType(null));
                            loadAndOpenExistingTemplate(templateId, fabricId, templateName);
                          }}
                        />
                      )}
                      </>
                    ) : openTemplateTabs.length > 0 ? (
                      <>
                        {/* Tab content with side navigation */}
                        {activeTabId && (
                          <div className="row ev-card-4 dashboard-right-main w-100 border-0 m-0 ev-padding-small vh-80">
                            {/* Side navigation pane */}
                            <div className="col-1 ps-0 style={{ maxWidth: '50px' }}">
                              <div className="ev-card-4 dashboard-right-main-sidebar w-100 ev-radius-small vh-75">
                                <div className="d-flex flex-column align-items-center">
                                  <button
                                    className={`ev-btn-lightblue-fill-round ev-small-p-blue dashboard-right-main-sidebar-btn avatar-tooltip text-center my-2 ${getActiveViewForTab(activeTabId) === 'template' ? 'active' : ''}`}
                                    data-tooltip="BI Suite"
                                    onClick={() => setActiveViewForTab(activeTabId, 'template')}
                                  >
                                    <i className="bi bi-kanban-fill"></i>
                                  </button>

                                  {/* Only show Value Messages if vm_config is true */}
                                  {tabVmConfig[activeTabId] === true && (
                                    <button
                                      className={`ev-btn-lightblue-fill-round ev-small-p-blue dashboard-right-main-sidebar-btn avatar-tooltip text-center my-2 ${getActiveViewForTab(activeTabId) === 'valuemessage' ? 'active' : ''}`}
                                      data-tooltip="Value Messages"
                                      onClick={() => setActiveViewForTab(activeTabId, 'valuemessage')}
                                    >
                                      <i className="bi bi-chat-dots-fill"></i>
                                    </button>
                                  )}

                                  {/* Report button */}
                                  <button
                                    className={`ev-btn-lightblue-fill-round ev-small-p-blue dashboard-right-main-sidebar-btn avatar-tooltip text-center my-2 ${getActiveViewForTab(activeTabId) === 'report' ? 'active' : ''}`}
                                    data-tooltip="Manage Reports"
                                    onClick={() => setActiveViewForTab(activeTabId, 'report')}
                                  >
                                    <i className="bi bi-file-text-fill"></i>
                                  </button>
                                </div>
                              </div>
                            </div>
                            {/* Main content area */}
                            <div className="col-11 ps-0  pe-0">
                              <div className="w-100 position-relative dashboard-right-main-contentarea">
                                {renderTabContent(activeTabId)}
                              </div>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <GenbiLandingComponent />
                    )}
                  </div>
                </div>{/* end genbi-main-content-col */}
            </div>{/* end genbi-layout-row */}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        show={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setTemplateToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        objectName="template"
        itemName={templateToDelete?.template_name || ''}
        bodyContent={templateToDelete && (
          <div>
            <div className="mb-2">
              <strong>Fabric:</strong> {templateToDelete.fabric_name}
            </div>
            <div className="mb-2">
              <strong>Agents:</strong> {templateToDelete.agent_count || 0}
            </div>
            <div className="mb-3">
              <strong>Sections:</strong> {templateToDelete.section_count || 0}
            </div>
            {editingTemplate?.id === templateToDelete.id && (
              <div className="alert alert-warning" role="alert">
                <i className="bi bi-exclamation-triangle me-2"></i>
                <strong>Warning:</strong> You are currently editing this template. Deleting it will close the form.
              </div>
            )}
            <p className="text-danger mb-0">This will permanently delete the template and all its sections and agents. This action cannot be undone.</p>
          </div>
        )}
      />

      {/* Popups */}
      {isProcessingPopupVisible && (
        <ProcessingPopup message={processingMessage} />
      )}

      {isErrorPopupVisible && (
        <ErrorPopup
          onClose={closeErrorPopup}
          message={popupErrorMessage}
        />
      )}

      {isSuccessPopupVisible && (
        <SuccessPopup
          onClose={closeSuccessPopup}
          message={popupSuccessMessage}
        />
      )}
    </React.Fragment>
  );
};

GenbiWorkspace.displayName = 'GenbiWorkspace';

export default GenbiWorkspace;